/**
 * 내 자료·나눔터 상세가 같이 쓰는 가족 미리보기.
 * 텍스트 쪽(page_urls 우선 → 캔버스), [학생용|정답지], PPT 슬라이드, 이미지 캐러셀.
 */

import React, { useEffect, useRef, useState } from 'react';
import HtmlSlideRenderer from '../chat/slides/HtmlSlideRenderer';
import LibraryAnswerKeySwitch from './LibraryAnswerKeySwitch';
import LibraryVersionSwitch from './LibraryVersionSwitch';
import {
  LAYOUT_LABELS,
  Q_TYPE_LABELS,
  SECTION_TYPE_LABELS,
} from './libraryOutlineLabels';

export function LibraryFamilyCarousel({ urls, title, resetKey }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const mouseStartX = useRef(0);
  const mouseDragging = useRef(false);
  const firstUrl = urls?.[0] || '';

  useEffect(() => {
    setIndex(0);
  }, [resetKey, firstUrl]);

  if (!urls?.length) return null;
  const safeIndex = Math.min(index, urls.length - 1);

  const handleTouchStart = (event) => { touchStartX.current = event.touches[0].clientX; };
  const handleTouchEnd = (event) => {
    const dx = event.changedTouches[0].clientX - touchStartX.current;
    if (dx < -40 && safeIndex < urls.length - 1) setIndex(safeIndex + 1);
    if (dx > 40 && safeIndex > 0) setIndex(safeIndex - 1);
  };
  const handleMouseDown = (event) => { mouseStartX.current = event.clientX; mouseDragging.current = true; };
  const handleMouseUp = (event) => {
    if (!mouseDragging.current) return;
    const dx = event.clientX - mouseStartX.current;
    if (dx < -40 && safeIndex < urls.length - 1) setIndex(safeIndex + 1);
    if (dx > 40 && safeIndex > 0) setIndex(safeIndex - 1);
    mouseDragging.current = false;
  };

  return (
    <div
      className="gallery-detail-carousel"
      onMouseDown={handleMouseDown}
      onMouseLeave={() => { mouseDragging.current = false; }}
      onMouseUp={handleMouseUp}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
      style={{ cursor: urls.length > 1 ? 'grab' : 'default', userSelect: 'none' }}
    >
      <img
        alt={`${title} ${safeIndex + 1}`}
        className="gallery-detail-carousel-img"
        draggable={false}
        src={urls[safeIndex]}
      />
      {urls.length > 1 && (
        <div className="gallery-carousel-dots-new">
          {urls.map((_, i) => (
            <button
              aria-label={`${i + 1}번 미리보기`}
              className={`gallery-carousel-dot-new${i === safeIndex ? ' active' : ''}`}
              key={i}
              onClick={() => setIndex(i)}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  );
}

const OutlineList = ({ entries, kind }) => (
  <div className="gallery-detail-outline-list">
    {entries.map((entry, i) => (
      <div className="gallery-detail-outline-item" key={i}>
        <span className="gallery-detail-outline-num">{entry.number || entry.section_number || i + 1}</span>
        <span className="gallery-detail-outline-type">
          {kind === 'questions'
            ? (Q_TYPE_LABELS[entry.type] || entry.type || '문항')
            : (SECTION_TYPE_LABELS[entry.section_type] || entry.section_type || '활동')}
        </span>
        <div className="gallery-detail-outline-content">
          <span className="gallery-detail-outline-title">
            {entry.question || entry.title || (kind === 'questions' ? `문항 ${i + 1}` : `섹션 ${i + 1}`)}
          </span>
        </div>
      </div>
    ))}
  </div>
);

export default function LibraryFamilyPreview({
  item,
  view,
  viewMode,
  onViewModeChange,
  pageKind,
  onPageKindChange,
  onOpenFullscreen,
}) {
  const handlePageKind = (next) => {
    onPageKindChange(next);
    onOpenFullscreen(null);
  };

  return (
    <>
      {view.isFamily && (
        <LibraryVersionSwitch
          item={item}
          onChange={onViewModeChange}
          value={viewMode}
        />
      )}

      {item?.content_type === 'worksheet' && (
        <LibraryAnswerKeySwitch
          hasAnswers={view.hasStoredAnswers}
          onChange={handlePageKind}
          value={pageKind}
        />
      )}

      {view.showAnswerKey && (
        <div className="gallery-detail-outline">
          <p className="gallery-detail-outline-label">
            {view.answerKeyPages.length ? `${view.answerKeyPages.length}쪽 정답지` : '정답지를 만들고 있어요'}
          </p>
          {view.answerKeyPages.length > 0 && (
            <div className="gallery-detail-slide-grid" data-testid="library-answer-pages">
              {view.answerKeyPages.map((slide, i) => (
                <button
                  aria-label={`정답지 ${i + 1}쪽 크게 보기`}
                  className="gallery-detail-slide-card"
                  data-testid="library-answer-page"
                  key={slide.slide_id || i}
                  onClick={() => onOpenFullscreen(i)}
                  type="button"
                >
                  <div className="gallery-detail-slide-frame">
                    <HtmlSlideRenderer slide={slide} themeId={view.answerKeyThemeId || view.worksheetThemeId} />
                  </div>
                  <span className="gallery-detail-slide-num">{i + 1}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {view.pptImagePageUrls?.length > 0 && (
        <div className="gallery-detail-outline">
          <p className="gallery-detail-outline-label">{`이미지형 PPT ${view.pptImagePageUrls.length}장`}</p>
          <div className="gallery-detail-slide-grid" data-testid="library-image-pages">
            {view.pptImagePageUrls.map((url, i) => (
              <button
                aria-label={`${i + 1}번 슬라이드 크게 보기`}
                className="gallery-detail-slide-card"
                data-testid="library-image-page"
                key={url || i}
                onClick={() => onOpenFullscreen(i)}
                type="button"
              >
                <div className="gallery-detail-slide-frame">
                  <img alt={`${item.title} ${i + 1}번 슬라이드`} loading="lazy" src={url} />
                </div>
                <span className="gallery-detail-slide-num">{i + 1}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {view.showTextViewer && item?.content_type === 'ppt' && view.designPageUrls?.length > 0 && (
        <div className="gallery-detail-outline">
          <p className="gallery-detail-outline-label">{`텍스트형 PPT ${view.designPageUrls.length}장`}</p>
          <div className="gallery-detail-slide-grid" data-testid="library-design-pages">
            {view.designPageUrls.map((url, i) => (
              <button
                aria-label={`${i + 1}번 슬라이드 크게 보기`}
                className="gallery-detail-slide-card"
                data-testid="library-design-page"
                key={url || i}
                onClick={() => onOpenFullscreen(i)}
                type="button"
              >
                <div className="gallery-detail-slide-frame">
                  <img alt={`${item.title} ${i + 1}번 슬라이드`} loading="lazy" src={url} />
                </div>
                <span className="gallery-detail-slide-num">{i + 1}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {view.showTextViewer && item?.content_type === 'ppt' && !(view.designPageUrls?.length > 0) && view.slides.length > 0 && (
        <div className="gallery-detail-outline">
          <p className="gallery-detail-outline-label">{`${view.slides.length}장 슬라이드 구성`}</p>
          <div className="gallery-detail-slide-grid">
            {view.slides.map((slide, i) => (
              <button
                aria-label={`${i + 1}번 슬라이드 크게 보기`}
                className="gallery-detail-slide-card"
                key={i}
                onClick={() => onOpenFullscreen(i)}
                type="button"
              >
                <div className="gallery-detail-slide-frame">
                  <HtmlSlideRenderer slide={slide} themeId={view.outline?.canvas_theme} />
                </div>
                <span className="gallery-detail-slide-num">{i + 1}</span>
              </button>
            ))}
          </div>
          <details className="gallery-detail-outline-details">
            <summary className="gallery-detail-outline-summary">슬라이드 목록 (텍스트)</summary>
            <div className="gallery-detail-outline-list">
              {view.slides.map((slide, i) => (
                <div className="gallery-detail-outline-item" key={i}>
                  <span className="gallery-detail-outline-num">{i + 1}</span>
                  <span className="gallery-detail-outline-type">{LAYOUT_LABELS[slide.layout] || '내용'}</span>
                  <div className="gallery-detail-outline-content">
                    <span className="gallery-detail-outline-title">{slide.title || slide.heading || `슬라이드 ${i + 1}`}</span>
                  </div>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {view.showWsPageImages && (
        <div className="gallery-detail-outline">
          <p className="gallery-detail-outline-label">{`${view.textPageUrls.length}쪽 구성`}</p>
          <div className="gallery-detail-slide-grid" data-testid="library-text-pages">
            {view.textPageUrls.map((url, i) => (
              <button
                aria-label={`${i + 1}쪽 크게 보기`}
                className="gallery-detail-slide-card"
                data-testid="library-text-page"
                key={url || i}
                onClick={() => onOpenFullscreen(i)}
                type="button"
              >
                <div className="gallery-detail-slide-frame">
                  <img alt={`${item.title} ${i + 1}쪽`} src={url} />
                </div>
                <span className="gallery-detail-slide-num">{i + 1}</span>
              </button>
            ))}
          </div>
          {(view.questions.length > 0 || view.sections.length > 0) && (
            <details className="gallery-detail-outline-details">
              <summary className="gallery-detail-outline-summary">
                {view.questions.length > 0 ? '문항 목록 (텍스트)' : '섹션 목록 (텍스트)'}
              </summary>
              <OutlineList
                entries={view.questions.length > 0 ? view.questions : view.sections}
                kind={view.questions.length > 0 ? 'questions' : 'sections'}
              />
            </details>
          )}
        </div>
      )}

      {view.showWsCanvasPages && (
        <div className="gallery-detail-outline">
          <p className="gallery-detail-outline-label">{`${view.worksheetPages.length}쪽 구성`}</p>
          <div className="gallery-detail-slide-grid" data-testid="library-text-pages">
            {view.visibleWorksheetPages.map((slide, i) => (
              <button
                aria-label={`${i + 1}쪽 크게 보기`}
                className="gallery-detail-slide-card"
                data-testid="library-text-page"
                key={slide.slide_id || i}
                onClick={() => onOpenFullscreen(i)}
                type="button"
              >
                <div className="gallery-detail-slide-frame">
                  <HtmlSlideRenderer slide={slide} themeId={view.worksheetThemeId} />
                </div>
                <span className="gallery-detail-slide-num">{i + 1}</span>
              </button>
            ))}
          </div>
          {(view.questions.length > 0 || view.sections.length > 0) && (
            <details className="gallery-detail-outline-details">
              <summary className="gallery-detail-outline-summary">
                {view.questions.length > 0 ? '문항 목록 (텍스트)' : '섹션 목록 (텍스트)'}
              </summary>
              <OutlineList
                entries={view.questions.length > 0 ? view.questions : view.sections}
                kind={view.questions.length > 0 ? 'questions' : 'sections'}
              />
            </details>
          )}
        </div>
      )}

      {view.showTextViewer && !view.showAnswerKey && item?.content_type === 'worksheet' && !view.showWsPageImages && !view.showWsCanvasPages && view.worksheetPending && (
        <p className="gallery-detail-outline-label">쪽 미리보기를 만들고 있어요</p>
      )}

      {view.showTextViewer && !view.showAnswerKey && item?.content_type === 'worksheet' && !view.showWsPageImages && !view.showWsCanvasPages && !view.worksheetPending && view.questions.length > 0 && (
        <div className="gallery-detail-outline">
          <p className="gallery-detail-outline-label">{view.questions.length}문항 구성</p>
          <OutlineList entries={view.questions} kind="questions" />
        </div>
      )}

      {view.showTextViewer && !view.showAnswerKey && item?.content_type === 'worksheet' && !view.showWsPageImages && !view.showWsCanvasPages && view.questions.length === 0 && view.sections.length > 0 && (
        <div className="gallery-detail-outline">
          <p className="gallery-detail-outline-label">{view.sections.length}개 섹션 구성</p>
          <OutlineList entries={view.sections} kind="sections" />
        </div>
      )}
    </>
  );
}
